// Утилиты для экспорта результатов отчетов в различные форматы
import { getFieldDisplayName } from './fieldMapping';

/**
 * Экспорт данных отчета в CSV
 * @param {Array} data - обработанные данные отчета
 * @param {Object} templateConfig - конфигурация шаблона
 * @param {string} filename - имя файла
 */
export const exportToCSV = (data, templateConfig, filename = 'report') => {
  if (!data || data.length === 0) {
    alert('Нет данных для экспорта');
    return;
  }

  try {
    // Определяем колонки для экспорта
    const columns = getExportColumns(data, templateConfig);

    // Создаем заголовки CSV
    const headers = columns.map(col => `"${col.label}"`).join(',');

    // Создаем строки данных
    const rows = data.map(item => {
      return columns.map(col => {
        let value = item[col.key];

        // Форматируем значение
        if (value === null || value === undefined) {
          value = '';
        } else if (typeof value === 'number') {
          value = value.toString();
        } else if (typeof value === 'object') {
          value = JSON.stringify(value);
        } else {
          value = String(value);
        }

        // Экранируем кавычки и оборачиваем в кавычки
        value = value.replace(/"/g, '""');
        return `"${value}"`;
      }).join(',');
    });

    // Объединяем все в CSV
    const csv = [headers, ...rows].join('\n');

    // Добавляем BOM для корректного отображения кириллицы в Excel
    const BOM = '\uFEFF';
    const csvContent = BOM + csv;

    // Создаем и скачиваем файл
    downloadFile(csvContent, `${filename}.csv`, 'text/csv;charset=utf-8;');

  } catch (error) {
    console.error('Ошибка экспорта в CSV:', error);
    alert('Ошибка при экспорте данных: ' + error.message);
  }
};

/**
 * Экспорт данных отчета в Excel (HTML таблица)
 * @param {Array} data - обработанные данные отчета
 * @param {Object} templateConfig - конфигурация шаблона
 * @param {string} filename - имя файла
 */
export const exportToExcel = (data, templateConfig, filename = 'report') => {
  if (!data || data.length === 0) {
    alert('Нет данных для экспорта');
    return;
  }

  try {
    // Определяем колонки для экспорта
    const columns = getExportColumns(data, templateConfig);

    // Создаем HTML таблицу
    let html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="UTF-8">
        <style>
          table { border-collapse: collapse; width: 100%; }
          th { background-color: #4472C4; color: white; font-weight: bold; padding: 8px; border: 1px solid #ddd; }
          td { padding: 8px; border: 1px solid #ddd; }
          tr:nth-child(even) { background-color: #f2f2f2; }
        </style>
      </head>
      <body>
        <table>
          <thead>
            <tr>
    `;

    // Добавляем заголовки
    columns.forEach(col => {
      html += `<th>${escapeHtml(col.label)}</th>`;
    });

    html += `
            </tr>
          </thead>
          <tbody>
    `;

    // Добавляем строки данных
    data.forEach(item => {
      html += '<tr>';
      columns.forEach(col => {
        let value = item[col.key];

        // Форматируем значение
        if (value === null || value === undefined) {
          value = '';
        } else if (typeof value === 'number') {
          value = value.toLocaleString('ru-RU');
        } else if (typeof value === 'object') {
          value = JSON.stringify(value);
        } else {
          value = String(value);
        }

        html += `<td>${escapeHtml(value)}</td>`;
      });
      html += '</tr>';
    });

    html += `
          </tbody>
        </table>
      </body>
      </html>
    `;

    // Создаем и скачиваем файл
    downloadFile(html, `${filename}.xls`, 'application/vnd.ms-excel');

  } catch (error) {
    console.error('Ошибка экспорта в Excel:', error);
    alert('Ошибка при экспорте данных: ' + error.message);
  }
};

/**
 * Экспорт данных отчета в JSON
 * @param {Array} data - обработанные данные отчета
 * @param {Object} templateConfig - конфигурация шаблона
 * @param {string} filename - имя файла
 */
export const exportToJSON = (data, templateConfig, filename = 'report') => {
  if (!data || data.length === 0) {
    alert('Нет данных для экспорта');
    return;
  }

  try {
    // Создаем объект для экспорта с метаданными
    const exportData = {
      template: {
        name: templateConfig.name,
        description: templateConfig.description,
        dateRange: templateConfig.dateRange,
        generatedAt: new Date().toISOString()
      },
      summary: {
        totalRecords: data.length,
        totalGroups: data.filter(item => item.groupKey).length
      },
      data: data.map(item => {
        // Создаем копию объекта без массива items (если он большой)
        const exportItem = { ...item };

        // Если есть items и их много, оставляем только count
        if (exportItem.items && exportItem.items.length > 100) {
          exportItem.itemsCount = exportItem.items.length;
          delete exportItem.items;
        }

        return exportItem;
      })
    };

    const json = JSON.stringify(exportData, null, 2);

    // Создаем и скачиваем файл
    downloadFile(json, `${filename}.json`, 'application/json');

  } catch (error) {
    console.error('Ошибка экспорта в JSON:', error);
    alert('Ошибка при экспорте данных: ' + error.message);
  }
};

/**
 * Экспорт детальных данных (включая все записи в группах)
 * @param {Array} data - обработанные данные отчета
 * @param {Object} templateConfig - конфигурация шаблона
 * @param {string} filename - имя файла
 */
export const exportDetailedCSV = (data, templateConfig, filename = 'report_detailed') => {
  if (!data || data.length === 0) {
    alert('Нет данных для экспорта');
    return;
  }

  try {
    // Собираем все записи из всех групп
    const allItems = [];

    data.forEach(group => {
      if (group.items && group.items.length > 0) {
        group.items.forEach(item => {
          allItems.push({
            ...item,
            _groupKey: group.groupKey || 'Без группы'
          });
        });
      } else {
        // Если нет items, значит это одиночная запись
        allItems.push({
          ...group,
          _groupKey: group.groupKey || 'Без группы'
        });
      }
    });

    if (allItems.length === 0) {
      alert('Нет детальных данных для экспорта');
      return;
    }

    // Определяем колонки из первой записи
    const firstItem = allItems[0];
    const columns = Object.keys(firstItem)
      .filter(key => !['items', 'count'].includes(key))
      .map(key => ({
        key: key,
        label: key === '_groupKey' ? 'Группа' : getFieldDisplayName(key)
      }));

    // Создаем заголовки CSV
    const headers = columns.map(col => `"${col.label}"`).join(',');

    // Создаем строки данных
    const rows = allItems.map(item => {
      return columns.map(col => {
        let value = item[col.key];

        // Форматируем значение
        if (value === null || value === undefined) {
          value = '';
        } else if (typeof value === 'number') {
          value = value.toString();
        } else if (typeof value === 'object') {
          value = JSON.stringify(value);
        } else {
          value = String(value);
        }

        // Экранируем кавычки и оборачиваем в кавычки
        value = value.replace(/"/g, '""');
        return `"${value}"`;
      }).join(',');
    });

    // Объединяем все в CSV
    const csv = [headers, ...rows].join('\n');

    // Добавляем BOM для корректного отображения кириллицы в Excel
    const BOM = '\uFEFF';
    const csvContent = BOM + csv;

    // Создаем и скачиваем файл
    downloadFile(csvContent, `${filename}.csv`, 'text/csv;charset=utf-8;');

  } catch (error) {
    console.error('Ошибка экспорта детальных данных:', error);
    alert('Ошибка при экспорте данных: ' + error.message);
  }
};

/**
 * Получение колонок для экспорта
 */
const getExportColumns = (data, templateConfig) => {
  const columns = [];

  // Добавляем колонку группы, если есть группировка
  if (data[0]?.groupKey) {
    columns.push({
      key: 'groupKey',
      label: 'Группа'
    });
  }

  // Добавляем колонку количества
  if (data[0]?.count !== undefined) {
    columns.push({
      key: 'count',
      label: 'Количество записей'
    });
  }

  // Добавляем агрегированные поля
  if (templateConfig.aggregations && templateConfig.aggregations.length > 0) {
    templateConfig.aggregations.forEach(agg => {
      if (agg.outputField) {
        columns.push({
          key: agg.outputField,
          label: agg.label || getFieldDisplayName(agg.field)
        });
      }
    });
  }

  // Добавляем другие поля из первой записи
  if (data.length > 0) {
    const firstItem = data[0];
    Object.keys(firstItem).forEach(key => {
      // Пропускаем уже добавленные и служебные поля
      if (!columns.find(c => c.key === key) &&
          !['items', 'groupKey', 'count'].includes(key)) {
        columns.push({
          key: key,
          label: getFieldDisplayName(key)
        });
      }
    });
  }

  return columns;
};

/**
 * Экранирование HTML
 */
const escapeHtml = (text) => {
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return String(text).replace(/[&<>"']/g, m => map[m]);
};

/**
 * Скачивание файла
 */
const downloadFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};